import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  CreditCard, 
  ExternalLink,
  History,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { useTitle } from '../../../context/TitleContext';
import { useToast } from '../../../context/ToastContext';
import { SubscriptionBadge } from '../SubscriptionBadge';
import { TrialProgressBar } from '../TrialProgressBar';
import { ExtendTrialModal } from '../ExtendTrialModal';
import { 
  getOrganizationById, 
  getAuditLog,
  updateSubscriptionStatus 
} from '../../../lib/adminQueries';
import { OrganizationWithSubscription, AuditLogEntry } from '../../../models/admin';
import { SubscriptionStatus } from '../../../lib/supabase';

export const OrganizationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { showToast } = useToast();
  const [organization, setOrganization] = useState<OrganizationWithSubscription | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    setTitle('Organization Details');
    document.title = 'Organization Details';
  }, [setTitle]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const [orgData, logData] = await Promise.all([
          getOrganizationById(id),
          getAuditLog(id, 'organization'),
        ]);
        
        setOrganization(orgData);
        setAuditLog(logData);
        
        if (orgData) {
          setTitle(orgData.name);
          document.title = `${orgData.name} - Organization Details`;
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
        showToast('Failed to load organization details', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, setTitle, showToast]);

  const handleUpdateStatus = async (status: SubscriptionStatus) => {
    if (!organization || !id) return;

    setActionLoading(true);
    try {
      // TODO: Replace with actual admin user ID from auth context
      // Example: const { currentUser } = useAuth(); const adminUserId = currentUser.id;
      await updateSubscriptionStatus(id, status, 'admin-user-id');
      setOrganization({ ...organization, subscription_status: status });
      showToast(`Subscription status updated to ${status}`, 'success');
      
      // Refresh audit log
      const logData = await getAuditLog(id, 'organization');
      setAuditLog(logData);
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Failed to update subscription status', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrialExtended = async () => {
    if (!id) return;
    
    // Refresh organization data
    const orgData = await getOrganizationById(id);
    setOrganization(orgData);
    
    // Refresh audit log
    const logData = await getAuditLog(id, 'organization');
    setAuditLog(logData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-gray-600 dark:text-gray-400">Loading organization...</p>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">Organization not found</p>
          <button
            onClick={() => navigate('/admin/organizations')}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Back to Organizations
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/organizations')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Organizations
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <Building2 className="h-8 w-8 mr-3 text-blue-600" />
              {organization.name}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Created {format(new Date(organization.created_at), 'MMMM d, yyyy')}
            </p>
          </div>
          <SubscriptionBadge status={organization.subscription_status} size="lg" />
        </div>
      </div>

      {/* Quick Actions */}
      {organization.subscription_status === 'trial' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {organization.trial_ends_at && (
                <TrialProgressBar trialEndsAt={organization.trial_ends_at} />
              )}
            </div>
            <button
              onClick={() => setShowExtendTrialModal(true)}
              className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Extend Trial
            </button>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Organization Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Company Information
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{organization.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Customer ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {organization.stripe_customer_id ? (
                    <a
                      href={`https://dashboard.stripe.com/customers/${organization.stripe_customer_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                    >
                      {organization.stripe_customer_id}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  ) : (
                    'N/A'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Users</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {organization.userCount || 0} users
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Revenue</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">
                  {formatCurrency(organization.mrr)}
                </dd>
              </div>
              {organization.owner && (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Owner Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{organization.owner.full_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Owner Email</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{organization.owner.email}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* Subscription Details */}
          {organization.subscription && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Subscription Details
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Plan</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{organization.subscription.plan_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <SubscriptionBadge status={organization.subscription.status} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Period Ends</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {format(new Date(organization.subscription.current_period_end), 'MMMM d, yyyy')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Subscription</dt>
                  <dd className="mt-1 text-sm">
                    {organization.subscription.stripe_subscription_id ? (
                      <a
                        href={`https://dashboard.stripe.com/subscriptions/${organization.subscription.stripe_subscription_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                      >
                        View in Stripe
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Audit Log */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <History className="h-5 w-5 mr-2" />
              Audit Log
            </h2>
            {auditLog.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No audit log entries yet.</p>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry) => (
                  <div
                    key={entry.id}
                    className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-r"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          By {entry.admin_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                    {Object.keys(entry.changes).length > 0 && (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        <pre className="whitespace-pre-wrap">{JSON.stringify(entry.changes, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Admin Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Admin Actions
            </h2>
            <div className="space-y-3">
              {organization.subscription_status === 'trial' && (
                <button
                  onClick={() => handleUpdateStatus('active')}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  Convert to Paid
                </button>
              )}
              {organization.subscription_status === 'active' && (
                <button
                  onClick={() => handleUpdateStatus('canceled')}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  Cancel Subscription
                </button>
              )}
              {organization.subscription_status === 'past_due' && (
                <button
                  onClick={() => handleUpdateStatus('active')}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Mark as Paid
                </button>
              )}
              <button
                onClick={() => navigate(`/admin/users?org=${organization.id}`)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                View Users
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Stats
            </h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">Days as Customer</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.floor((Date.now() - new Date(organization.created_at).getTime()) / (1000 * 60 * 60 * 24))}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">Total Users</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">
                  {organization.userCount || 0}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showExtendTrialModal && (
        <ExtendTrialModal
          isOpen={showExtendTrialModal}
          onClose={() => setShowExtendTrialModal(false)}
          organizationId={organization.id}
          organizationName={organization.name}
          currentTrialEnd={organization.trial_ends_at}
          onSuccess={handleTrialExtended}
        />
      )}
    </div>
  );
};
